/**
 * The Formular view — the same sections, labels and field order as the
 * single-file predecessor, rendered from React on top of the PlanStore.
 * A wide PANE gets the full route table; ≤880px of pane gets per-leg cards —
 * pane, not window, because an open map can leave the form 500px on a tablet.
 *
 * Every caption goes through the i18n catalogue; the field ids, the stored
 * string formats and the PDF stay German whatever the interface says.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AIRFIELDS } from '@avia/core';
import { type Aircraft } from '../api';
import { LEG_NUMBERS, SECONDARY_SECTIONS, SECTIONS } from '../fields';
import { useT, type T } from '../i18n';
import { usePaneNarrow } from '../media';
import { useRoute, useTerrainStatus, useWindsStatus, useWxStatus } from '../planContext';
import { storedRows } from '../terrain';
import { useTerrain } from '../terrainContext';
import { useWinds } from '../windsContext';
import {
  ATIS_ROWS, briefingFor, lineKey, statusView, visibMetric, zHour, zTime,
  type FlightCategory, type NearbyFound, type TafPeriod, type WxParsed,
} from '../wx';
import { useWx } from '../wxContext';
import { windsHourText } from '../store';
import { Freshness } from './Freshness';
import { CheckField, DateField, FF, FieldInput, Lines, Row, SelectField, useReadOnly, type FieldOption } from './Field';

/* ---------------- option lists ---------------- */

/** Belag (surface): the AIP abbreviations the example plan uses, plus the words. */
const belagOptions = (t: T): FieldOption[] => [
  { value: 'ASPH', text: t('belag.asph') },
  { value: 'Beton', text: t('belag.beton') },
  { value: 'Gras', text: t('belag.gras') },
  { value: 'Schotter', text: t('belag.schotter') },
  { value: 'Sand', text: t('belag.sand') },
];

/** Grasbahn: the paper form carries an "x" in the column, nothing means no. */
const grasOptions = (t: T): FieldOption[] => [{ value: 'x', text: t('yes') }];

const MASSE_OPTIONS: readonly FieldOption[] = [{ value: 'kg' }, { value: 'lbs.' }];
const MOMENT_OPTIONS: readonly FieldOption[] = [{ value: 'x 100' }, { value: 'x 1000' }, { value: 'kgm' }];

/** ATIS cloud groups — the prefixes a pilot writes before the height. */
const WOLKEN_OPTIONS = ['CAVOK', 'NSC', 'NCD', 'SKC', 'FEW', 'SCT', 'BKN', 'OVC'];

/* ---------------- section shell ---------------- */

interface SecProps {
  sectionKey: string;
  open: boolean;
  onToggle(open: boolean): void;
  children: ReactNode;
  /** the freshness of whatever this section's data comes from — see `Freshness` */
  head?: ReactNode;
}

function Sec({ sectionKey, open, onToggle, children, head }: SecProps) {
  const t = useT();
  const meta = SECTIONS.find(s => s.key === sectionKey);
  return (
    <details className="card" id={'sec-' + sectionKey} open={open} onToggle={e => onToggle(e.currentTarget.open)}>
      <summary>
        <h2>
          <span>{t('sec.' + sectionKey + '.title')}</span>
          <span className="code">{meta?.code ?? ''}</span>
        </h2>
        {/* in the SUMMARY, so a folded section still says how old its data is */}
        {head ? <span className="sechead">{head}</span> : null}
      </summary>
      <div className="secbody">{children}</div>
    </details>
  );
}

/* ---------------- METAR / TAF ---------------- */

/**
 * "METAR LJLJ 10:20Z · TAF gültig bis 18Z · aktualisiert 10:31Z" — read from the
 * STORE (`plans.meta.wx`), not from the fetch, so the shared read-only view shows
 * the same sentence without a network call.
 *
 * What it says is when the observation was made, until when the forecast runs and
 * how old the answer is. WHO published it is not on this line and is not on any
 * line: that is the ⓘ's job, and on the shared page the ⓘ is the reader's only
 * way to it. The observation itself is in the form's cells, where the pilot can
 * see and correct it.
 */
function WxStatusLine({ alone = false }: { alone?: boolean }) {
  const t = useT();
  const status = useWxStatus();
  const view = useMemo(() => statusView(status), [status]);
  if (!view) return null;
  const parts = view.metars.map(m => t('wx.status.metar', { id: m.id, time: m.time }));
  if (view.taf) {
    // one TAF needs no name; with several, the line shows the one that ends first
    // and has to say which aerodrome that is
    parts.push(
      view.tafCount > 1
        ? t('wx.status.tafOf', { id: view.taf.id, time: view.taf.until })
        : t('wx.status.taf', { time: view.taf.until }),
    );
  }
  parts.push(t('fresh.at', { time: view.fetched }));
  // standing on its own (the shared view) it needs the separator the .wxbox
  // around it would otherwise have drawn
  return (
    <div className={'note wxstatus' + (alone ? ' wxalone' : '')}>
      <div>{parts.join(' · ')}</div>
      {/* The fallback is part of the provenance and not of the fetch, so that a shared
          plan which says "kein METAR für LJCE" does not stop there. It is printed only
          where the panel below is absent — in the editor the same sentence stands over
          the observation it is about, and twice is once too often. */}
      {alone && view.nearby.map(n => (
        <div key={n.for}>
          {t('wx.nearby.status', {
            of: n.for,
            station: n.name ? n.id + ' ' + n.name : n.id,
            dist: n.dist,
            dir: t('wx.dir.' + n.dir),
          })}
        </div>
      ))}
    </div>
  );
}

/* ---------------- flight categories: the coloured dots ---------------- */

/**
 * The dot in front of an observation or a forecast period.
 *
 * The four colours are the ones every briefing service draws — VFR green, MVFR blue,
 * IFR red, LIFR magenta — so a pilot reads them without a legend. The category code
 * is in the `title` and beside the dot in text as well, because colour alone is not
 * an interface: a red/green confusion is the commonest colour-vision deficiency there
 * is, and this is a weather minimum.
 */
function Cat({ cat }: { cat: FlightCategory | null }) {
  const t = useT();
  if (!cat) return null;
  const key = cat.toLowerCase();
  return (
    <span className={'wxcat wxcat-' + key} title={t('wx.cat.' + key)}>
      <span className="wxdot" aria-hidden="true" />
      {cat}
    </span>
  );
}

/**
 * One forecast period: when it applies, what changes, and how much of the sky it
 * leaves. The visibility is shown in metres and kilometres — the units SERA's
 * minima are written in — at the precision the decoding left it (see
 * {@link visibMetric}); a `9999` / CAVOK group says „at least" and is printed
 * that way rather than as a figure it does not guarantee.
 */
function TafLine({ p }: { p: TafPeriod }) {
  const t = useT();
  const bits: string[] = [];
  const span = [zHour(p.from), zHour(p.to)].filter(Boolean).join('–');
  if (span) bits.push(span);
  // NOAA reports a PROB30 group as change "PROB" with probability 30, so printing
  // both spells "PROB30 PROB"; and PROB30 TEMPO is a real and different group
  if (p.probability !== null) bits.push('PROB' + p.probability);
  if (p.change && p.change !== 'PROB') bits.push(p.change);
  const detail: string[] = [];
  if (p.visib !== null) {
    // „6+" is NOAA's decoding of 9999 / CAVOK / P6SM: a floor, not a figure
    detail.push(
      p.visibPlus
        ? t('wx.taf.visib.min', { v: '10 km' })
        : t('wx.taf.visib', { v: visibMetric(p.visib) }),
    );
  }
  if (p.ceilingFt !== null) detail.push(t('wx.taf.ceiling', { ft: p.ceilingFt }));
  if (p.wx) detail.push(p.wx);
  return (
    <div className="wxper">
      <Cat cat={p.category} />
      <span className="wxperwhen">{bits.join(' ')}</span>
      {detail.length ? <span className="note">{detail.join(' · ')}</span> : null}
    </div>
  );
}

/** The period list of one TAF, or nothing when NOAA decoded none. */
function TafPeriods({ p }: { p: Pick<WxParsed, 'periods'> }) {
  const t = useT();
  if (!p.periods.length) return null;
  return (
    <div className="wxtaf">
      <div className="note">{t('wx.taf.periods')}</div>
      {p.periods.map((per, i) => <TafLine key={i} p={per} />)}
    </div>
  );
}

/**
 * A station we have no observation for: say so, show the nearest one that DOES
 * report, and point at the official briefing.
 *
 * The order is the order a pilot needs it in. The gap first, because that is the
 * fact; the neighbour second, because it is the best available answer and is marked
 * as somebody else's; the official self-briefing last, because it is the only one of
 * the three that is a briefing.
 */
function NoMetar({ id, near }: { id: string; near: NearbyFound | undefined }) {
  const t = useT();
  const b = briefingFor(id);
  return (
    <div className="wxmiss">
      <div className="note">
        {b ? t('wx.nometar', { id }) : t('wx.nometar.generic', { id })}{' '}
        {/* a link the pilot follows — this app never fetches those services itself.
            The service NAME is the link text: the URL said the same thing twice
            and wrapped onto its own line on a phone. */}
        {b ? (
          <>
            {t('wx.nometar.brief')}{' '}
            <a href={b.url} target="_blank" rel="noreferrer noopener">
              {b.service}
            </a>
          </>
        ) : null}
      </div>
      {near ? <Nearby found={near} /> : null}
    </div>
  );
}

/**
 * The nearest reporting station, and where it was written down.
 *
 * Everything visible here says twice over that this is not the aerodrome the pilot
 * asked about: the heading names both places and the distance between them, and the
 * ATIS cell it fills does the same in the one place that survives onto paper.
 */
function Nearby({ found }: { found: NearbyFound }) {
  const t = useT();
  const wx = useWx();
  const p = found.parsed;
  const row = wx?.nearbyTaken[found.id];
  return (
    <div className="wxnear">
      <div className="wxnearhead">
        {t('wx.nearby.head', {
          of: found.for,
          // openAIP's name is decoration; the ICAO code is the identification, so the
          // code is always there and the name only when the mirror had one
          station: found.name ? found.id + ' ' + found.name : found.id,
          dist: Math.round(found.distNm),
          dir: t('wx.dir.' + found.dir),
        })}
      </div>
      {p.metarRaw ? (
        <div className="wxper">
          <Cat cat={p.category} />
          <pre>{p.metarRaw}</pre>
        </div>
      ) : null}
      <TafPeriods p={p} />
      {p.tafRaw ? <pre className="note">{p.tafRaw}</pre> : null}
      {wx && found.takeable && p.atis ? (
        row === undefined ? null : row ? (
          <div className="note">{t('wx.nearby.taken', { row, id: found.id, of: found.for })}</div>
        ) : (
          <div className="note wxerr">{t('wx.nearby.noroom')}</div>
        )
      ) : (
        <div className="note">
          {found.takeable ? null : t('wx.nearby.own', { id: found.id })}
        </div>
      )}
    </div>
  );
}

/**
 * The ATIS section's weather half: what the briefing found, and what it could not.
 *
 * There is no button. The briefing arrives by itself and says so in the section's
 * header (`<Freshness family="wx">`); what is left here is the part that needs
 * words — an aerodrome that publishes nothing, the neighbour that stands in for
 * it, and a third station the paper form has no row for.
 */
function AtisWx() {
  const t = useT();
  const readOnly = useReadOnly();
  const wx = useWx();
  // a shared plan carries the briefing's provenance but may not fetch anything
  if (readOnly || !wx) return <WxStatusLine alone />;
  const load = wx.load;
  const none = wx.stations.length === 0;
  return (
    <div className="wxbox">
      {none ? <div className="note">{t('wx.nostations')}</div> : null}
      {load?.unavailable.length ? (
        <div className="wxerr">{t('wx.unavailable', { ids: load.unavailable.join(', ') })}</div>
      ) : null}
      {load?.noRoom.length ? <div className="note">{t('wx.noroom', { ids: load.noRoom.join(', ') })}</div> : null}
      {load?.missing.map(id => (
        <NoMetar key={id} id={id} near={wx.nearby.find(n => n.for === id)} />
      ))}
      {wx.nearbyBusy ? <div className="note">{t('wx.nearby.busy')}</div> : null}
      <div className="note">{t('wx.hint')}</div>
      {load && load.parsed.some(p => p.metarRaw || p.tafRaw) ? (
        <details className="wxraw">
          <summary>{t('wx.raw')}</summary>
          {load.parsed
            .filter(p => p.metarRaw || p.tafRaw)
            .map(p => (
              <div key={p.id}>
                {p.metarRaw ? (
                  <div className="wxper">
                    <Cat cat={p.category} />
                    <pre>{p.metarRaw}</pre>
                  </div>
                ) : null}
                {p.tafRaw ? <pre>{p.tafRaw}</pre> : null}
                {/* the same dots on the forecast: a TAF whose periods are all VFR and
                    one with an IFR TEMPO in the middle read identically as raw text */}
                <TafPeriods p={p} />
              </div>
            ))}
        </details>
      ) : null}
    </div>
  );
}

/**
 * V6: what the briefing could NOT put into the Wetter-Beratung lines.
 *
 * The raw TAF and METAR are written there by the same pass that fills the ATIS
 * rows — a bulletin the pilot has to fetch twice is a bulletin they will fetch
 * once. The only thing left to say is the case the paper form imposes: six lines,
 * and a pilot who has filled them all keeps what they wrote.
 */
function WetterAuto() {
  const t = useT();
  const readOnly = useReadOnly();
  const wx = useWx();
  const dropped = wx?.pushed?.dropped ?? [];
  if (readOnly || !wx || dropped.length === 0) return null;
  return <div className="note wxerr">{t('wx.take.full', { lines: dropped.map(lineKey).join(', ') })}</div>;
}

/* ---------------- winds aloft (the route's W/V column) ---------------- */

/**
 * "aktualisiert 11:31Z · Vorhersage 12Z" — read from the STORE
 * (`plans.meta.winds`), like the METAR line, so the shared read-only view prints
 * it without a network call.
 *
 * Two facts, both the pilot's: how old the answer is, and which hour the forecast
 * is FOR. The model that produced it is not one of them — it changes nothing a
 * pilot decides — and the attribution the licence asks for is the linked line
 * under the block (`WindsCredit`), not a clause in this sentence.
 *
 * "Vorhersage 12Z" is the hour the forecast is for. The endpoint's `source_hour`
 * is the valid hour it interpolated, not the model run, and calling it a run
 * would be a different (and wronger) claim; the span "11Z–13Z" appears when the
 * fetch needed more than one hour, because naming only the first would print
 * leg 1's forecast over every leg's wind.
 */
function WindsStatusLine({ alone = false }: { alone?: boolean }) {
  const t = useT();
  const status = useWindsStatus();
  if (!status) return null;
  const hour = windsHourText(status);
  const parts = [
    t('fresh.at', { time: zTime(status.fetched_at) }),
    ...(hour ? [t('winds.status.hour', { hour })] : []),
  ];
  return <div className={'note wxstatus' + (alone ? ' wxalone' : '')}>{parts.join(' · ')}</div>;
}

/**
 * Open-Meteo's licence asks for a link beside any display of its data, so this
 * line stands wherever the forecast wind is shown: here, under the route table,
 * and again in the map's own panel. It is one line, it is the whole credit, and
 * everything else about the forecast — which model, which hour, how old — is in
 * the section header's ⓘ.
 */
function WindsCredit() {
  const t = useT();
  return (
    <p className="note wxcredit">
      <a className="cite" href="https://open-meteo.com/" target="_blank" rel="noopener noreferrer">
        {t('winds.credit')}
      </a>
    </p>
  );
}

/**
 * The route section's winds half: what the forecast could not answer, and the
 * credit its licence asks for.
 *
 * There is no „Wind laden“ any more — the W/V column fills itself whenever the
 * route or the departure hour moves, and the header says how old it is.
 */
function WindsBox() {
  const t = useT();
  const readOnly = useReadOnly();
  const w = useWinds();
  const status = useWindsStatus();
  // A shared plan carries the fill's provenance but may not fetch anything — and
  // it carries the HINT too. The reader of a share link is the one person who
  // cannot re-derive these numbers, so printing a forecast wind at them with no
  // statement that it is a planning aid is the wrong way round. Nothing is shown
  // when there is no fill: a lone caveat about winds that are not there is noise.
  if (readOnly || !w) {
    if (!status) return null;
    return (
      <>
        <WindsStatusLine alone />
        <div className="note">{t('winds.hint')}</div>
        <WindsCredit />
      </>
    );
  }
  const load = w.load;
  const legs = (list: readonly number[]) => list.join(', ');
  return (
    <div className="wxbox">
      {w.count === 0 ? <div className="note">{t('winds.nolegs')}</div> : null}
      {load?.now ? <div className="note">{t('winds.now.' + load.now)}</div> : null}
      {load?.unavailable.length ? (
        <div className="wxerr">{t('winds.unavailable', { legs: legs(load.unavailable) })}</div>
      ) : null}
      {load?.kept.length ? <div className="note">{t('winds.kept', { legs: legs(load.kept) })}</div> : null}
      {load?.dropped ? <div className="note">{t('winds.dropped', { n: load.dropped })}</div> : null}
      {w.stale.size ? <div className="note">{t('winds.stale.hint', { legs: legs([...w.stale]) })}</div> : null}
      <div className="note">{t('winds.hint')}</div>
      <WindsCredit />
    </div>
  );
}

/* ---------------- terrain (the route's SafeAlt column) ---------------- */

/**
 * "aktualisiert 12:15Z · Korridor 5 NM" — read from the STORE
 * (`plans.meta.terrain`), like the METAR and the winds lines, so the shared
 * read-only view prints it without a network call.
 *
 * The corridor stays on the line because it is what makes the suggested
 * Sicherheitshöhe checkable: the same leg over a 2 NM corridor and over a 10 NM
 * one are two different numbers, both correct. Who measured the ground is behind
 * the ⓘ, with the link Mapterhorn's terms ask for.
 */
function TerrainStatusLine({ alone = false }: { alone?: boolean }) {
  const t = useT();
  const status = useTerrainStatus();
  if (!status) return null;
  const parts = [t('fresh.at', { time: zTime(status.fetched_at) })];
  if (status.corridor_nm != null) parts.push(t('terrain.status.corridor', { nm: status.corridor_nm }));
  return <div className={'note wxstatus' + (alone ? ' wxalone' : '')}>{parts.join(' · ')}</div>;
}

/**
 * What a plan REMEMBERS about the ground under it: one line per leg, highest
 * terrain and suggested SafeAlt. It is the whole terrain half of the shared
 * read-only view — that page fetches nothing, and a Sicherheitshöhe printed with
 * no terrain figure beside it is a number nobody downstream can check.
 */
function TerrainSummary() {
  const t = useT();
  const status = useTerrainStatus();
  const route = useRoute();
  const rows = useMemo(
    () => storedRows(status, route?.waypoints ?? []),
    [status, route],
  );
  if (rows.length === 0) return null;
  return (
    <ul className="tsum">
      {rows.map(r => (
        <li key={r.leg}>
          <b>{t('terrain.leg', { n: r.leg, from: r.from, to: r.to })}</b>{' '}
          <span className="note">
            {r.maxTerrainFt == null
              ? t('terrain.nodata')
              : t('terrain.max.plain', { ft: r.maxTerrainFt })}
            {r.msaFt == null
              ? ''
              : ' · ' +
                t('terrain.msa', { ft: r.msaFt }) +
                ' ' +
                t(r.msaRule === 'mountain' ? 'terrain.rule.mountain' : 'terrain.rule.flat')}
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * The route section's terrain half: which legs kept the pilot's own
 * Sicherheitshöhe, and the sentence that says what the suggestion is worth.
 *
 * The suggestion itself is already in the column — it is written by the same pass
 * that reads the profile, like the forecast wind in the W/V column, and it is
 * overruled by typing over it. So there is nothing to press here; what is left is
 * what needs words.
 */
function TerrainBox() {
  const t = useT();
  const readOnly = useReadOnly();
  const terrain = useTerrain();
  const status = useTerrainStatus();
  // a shared plan carries the profile's summary and attribution but may not fetch
  if (readOnly || !terrain) {
    // the same bargain as WindsBox: the summary travels, and so does the sentence
    // that says a Sicherheitshöhe out of a DEM is a suggestion and not a clearance
    if (!status) return null;
    return (
      <>
        <TerrainSummary />
        <TerrainStatusLine alone />
        <div className="note">{t('terrain.hint')}</div>
      </>
    );
  }
  return (
    <div className="wxbox">
      {/* „Keine Abschnitte" is the winds box's sentence: the two blocks sit under one
          table and one of them saying it is enough */}
      {terrain.kept.length ? (
        <div className="note">{t('terrain.kept', { legs: terrain.kept.join(', ') })}</div>
      ) : null}
      <div className="note">{t('terrain.hint')}</div>
    </div>
  );
}

/* ---------------- aircraft picker ---------------- */

/**
 * Which aircraft the plan is flown with. Picking one does NOT write anything
 * here: it hands the id up to the editor, which stores it on the plan and lets
 * the PlanStore's feed fill Muster, Kennzeichen, TAS, tank figures and the empty
 * mass — so every one of those cells carries the "from aircraft profile" mark
 * instead of looking like something the pilot typed.
 */
function AircraftPicker({
  aircraft,
  aircraftId,
  onPick,
}: {
  aircraft: Aircraft[];
  aircraftId: string | null;
  onPick?: ((id: string | null) => void) | undefined;
}) {
  const readOnly = useReadOnly();
  const t = useT();
  if (!onPick || readOnly) return null;
  // Nothing to pick from: a select whose only option is "keines" is noise next to the
  // link that actually helps ("Flugzeug anlegen →").
  if (!aircraft.length) {
    return (
      <Link className="acmanage" to="/aircraft">
        {t('form.aircraft.none')}
      </Link>
    );
  }
  return (
    <>
      <label className="ff">
        <span>{t('form.aircraft')}</span>
        <select
          value={aircraftId ?? ''}
          style={{ width: '170px' }}
          aria-label={t('form.aircraft.aria')}
          onChange={e => onPick(e.target.value || null)}
        >
          <option value="">{t('form.aircraft.empty')}</option>
          {aircraft.map(a => (
            <option key={a.id} value={a.id}>
              {a.registration}
              {a.type_code ? ' · ' + a.type_code : ''}
            </option>
          ))}
        </select>
      </label>
      <Link className="acmanage" to="/aircraft">
        {t('form.aircraft.manage')}
      </Link>
    </>
  );
}

/* ---------------- route ---------------- */

/** Column heads, in table order; '' is the row-label column. */
const ROUTE_HEADS = [
  '', 'th.cp', 'th.vor', 'th.safealt', 'th.alt', 'th.tas', 'th.wind', 'th.tc', 'th.var',
  'th.wca', 'th.mh', 'th.mc', 'th.dist', 'th.rest_dist', 'th.gs', 'th.time', 'th.rest_time',
  'th.eto', 'th.ato', 'th.remark',
];

const LEG_CELLS: { key: string; cls: string }[] = [
  { key: 'safealt', cls: 'cW-s' }, { key: 'alt', cls: 'cW-s' }, { key: 'tas', cls: 'cW-s' },
  { key: 'wind', cls: 'cW-wind' }, { key: 'tc', cls: 'cW-s' }, { key: 'var', cls: 'cW-s' },
  { key: 'wca', cls: 'cW-s' }, { key: 'mh', cls: 'cW-s' }, { key: 'mc', cls: 'cW-s' },
  { key: 'dist', cls: 'cW-s' }, { key: 'rest_dist', cls: 'cW-s' }, { key: 'gs', cls: 'cW-s' },
  { key: 'time', cls: 'cW-s' }, { key: 'rest_time', cls: 'cW-s' },
];

/** Per-leg card fields (narrow screens), in the predecessor's order. */
const LEG_CARD_FIELDS: [key: string, textKey: string, span: 1 | 2 | 4][] = [
  ['name', 'card.name', 2], ['freq', 'th.vor', 1], ['safealt', 'th.safealt', 1],
  ['alt', 'th.alt', 1], ['tas', 'th.tas', 1], ['wind', 'th.wind', 2],
  ['tc', 'th.tc', 1], ['var', 'th.var', 1], ['wca', 'th.wca', 1], ['mh', 'th.mh', 1],
  ['mc', 'th.mc', 1], ['dist', 'th.dist', 1], ['gs', 'th.gs', 1], ['time', 'th.time', 1],
  ['rest_dist', 'card.rest_dist', 1], ['rest_time', 'card.rest_time', 1], ['eto', 'th.eto', 1],
  ['ato', 'th.ato', 1], ['remark', 'leg.remark', 4],
];

const cardSpan = (n: 1 | 2 | 4): { span?: 2 | 4 } => (n === 1 ? {} : { span: n });

function RouteTable() {
  const t = useT();
  return (
    <div className="rtwrap">
      <table className="route">
        <thead>
          <tr>
            {ROUTE_HEADS.map((h, i) => (
              <th key={i}>{h ? t(h) : ''}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <span className="rowlab">{t('form.dep')}</span>
            </td>
            <td className="tl cW-name">
              <FieldInput id="cp1_name" list="icao-list" />
            </td>
            <td className="cW-freq">
              <FieldInput id="cp1_freq" />
            </td>
            {Array.from({ length: 17 }, (_, k) => (
              <td key={k} />
            ))}
          </tr>
          {LEG_NUMBERS.map(j => (
            <tr className="sub" key={j}>
              <td>
                <span className="rowlab">{j}</span>
              </td>
              <td className="tl cW-name">
                <FieldInput id={'cp' + (j + 1) + '_name'} list="icao-list" />
              </td>
              <td className="cW-freq">
                <FieldInput id={'cp' + (j + 1) + '_freq'} />
              </td>
              {LEG_CELLS.map(c => (
                <td className={c.cls} key={c.key}>
                  <FieldInput id={'leg' + j + '_' + c.key} />
                </td>
              ))}
              <td className="cW-m">
                <FieldInput id={'cp' + (j + 1) + '_eto'} />
              </td>
              <td className="cW-m">
                <FieldInput id={'cp' + (j + 1) + '_ato'} />
              </td>
              <td className="tl cW-rem">
                <FieldInput id={'leg' + j + '_remark'} />
              </td>
            </tr>
          ))}
          <tr className="totals">
            <td>
              <span className="rowlab">{t('form.total')}</span>
            </td>
            {Array.from({ length: 11 }, (_, k) => (
              <td key={k} />
            ))}
            <td className="cW-s">
              <FieldInput id="total_dist" />
            </td>
            <td />
            <td />
            <td className="cW-s">
              <FieldInput id="total_time" />
            </td>
            <td />
            <td />
            <td />
            <td />
          </tr>
          <tr className="totals">
            <td>
              <span className="rowlab">{t('form.alt')}</span>
            </td>
            <td className="tl cW-name">
              <FieldInput id="alt_name" list="icao-list" />
            </td>
            <td className="cW-freq">
              <FieldInput id="alt_freq" />
            </td>
            {LEG_CELLS.filter(c => c.key !== 'rest_dist' && c.key !== 'rest_time').map(c => (
              <td className={c.cls} key={c.key}>
                <FieldInput id={'alt_' + c.key} />
              </td>
            ))}
            <td />
            <td />
            <td />
            <td className="tl cW-rem">
              <FieldInput id="alt_remark" />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function LegCards() {
  const t = useT();
  return (
    <div className="legcards">
      <div className="legcard">
        <h4>{t('form.dep')}</h4>
        <div className="cardgrid">
          <FF id="cp1_name" text={t('card.dep')} span={2} list="icao-list" />
          <FF id="cp1_freq" text={t('th.vor')} span={2} />
        </div>
      </div>
      {LEG_NUMBERS.map(j => (
        <div className="legcard" key={j}>
          <h4>{t('form.leg', { n: j })}</h4>
          <div className="cardgrid">
            {LEG_CARD_FIELDS.map(([k, textKey, span]) => {
              const id = ['name', 'freq', 'eto', 'ato'].includes(k) ? 'cp' + (j + 1) + '_' + k : 'leg' + j + '_' + k;
              return (
                <FF key={k} id={id} text={t(textKey)} {...cardSpan(span)} {...(k === 'name' ? { list: 'icao-list' } : {})} />
              );
            })}
          </div>
        </div>
      ))}
      <div className="legcard">
        <h4>{t('form.total')}</h4>
        <div className="cardgrid">
          <FF id="total_dist" text={t('card.total_dist')} />
          <FF id="total_time" text={t('card.total_time')} />
          <FF id="eta" text={t('form.eta')} />
          <FF id="safe_time" text={t('form.safe_time')} />
        </div>
      </div>
      <div className="legcard">
        <h4>{t('lbl.alt_name')}</h4>
        <div className="cardgrid">
          {LEG_CARD_FIELDS.filter(([k]) => !['rest_dist', 'rest_time', 'eto', 'ato'].includes(k)).map(
            ([k, textKey, span]) => (
              <FF
                key={k}
                id={'alt_' + k}
                text={t(k === 'name' ? 'lbl.alt_name' : textKey)}
                {...cardSpan(span)}
                {...(k === 'name' ? { list: 'icao-list' } : {})}
              />
            ),
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------------- the form ---------------- */

/** AIP rows: the field-id fragment and the catalogue key of its row label. */
const AIP_ROWS: [key: string, textKey: string][] = [
  ['p1', 'form.dep'],
  ['p2f', 'form.dest'],
  ['p20', 'form.alt'],
];

const AIP_CELLS: [key: string, width: number][] = [
  ['rwy', 60], ['len', 70], ['dist15', 70], ['elev', 60], ['temp', 60], ['wind', 66], ['slope', 56],
];

const FUEL_ROWS: [key: string, zeit: boolean, liter: boolean][] = [
  ['reise', true, true],
  ['anlassen', false, true],
  ['steig', true, true],
  ['anab', true, true],
  ['altn', true, true],
  ['reserve', true, true],
  ['mindest', false, true],
  ['extra', true, true],
  ['vorrat', true, true],
];

/** Mass & balance rows; the two sum rows are printed bold with a leading "=". */
const MB_ROWS: [key: string, sum: boolean][] = [
  ['leer', false], ['kna', false], ['schmier', false], ['grund', true],
  ['sitz1', false], ['sitz2', false], ['sitz3', false],
  ['gep_vorn', false], ['gep_mitte', false], ['gep_hinten', false],
  ['kausf', false], ['start', true],
];

const TL_COLS = ['sr', 'ss', 'll', 'lr'];

const TL_ROWS: [key: string, cols: string[]][] = [
  ['bahn', TL_COLS],
  ['verf', ['ss']],
  ['grund', TL_COLS],
  ['hoehe', TL_COLS],
  ['temp', TL_COLS],
  ['wind', TL_COLS],
  ['neig', TL_COLS],
  ['gras', TL_COLS],
  ['oberfl', TL_COLS],
  ['benoetigt', TL_COLS],
];

/**
 * The LBA sheet itself. It no longer owns the weather provider — the workspace
 * mounts `WxProvider` around every pane, because the briefing is a property of
 * the flight and not of the tab that happens to show it.
 */
export function PlanForm({
  aircraft = [],
  aircraftId = null,
  onPickAircraft,
}: {
  aircraft?: Aircraft[];
  /** the plan's `aircraft_id`; the picker shows it and the store feeds from it */
  aircraftId?: string | null;
  /** absent in the read-only shared view and when signed out — then there is no picker */
  onPickAircraft?: ((id: string | null) => void) | undefined;
}) {
  /* the PANE's width, not the window's: beside an open map the form can be far
     narrower than the screen, and that is what decides table-vs-cards */
  const narrow = usePaneNarrow();
  const t = useT();
  const [userOpen, setUserOpen] = useState<Record<string, boolean>>({});

  const isOpen = (key: string): boolean => {
    const own = userOpen[key];
    if (own !== undefined) return own;
    return SECONDARY_SECTIONS.includes(key) ? !narrow : true;
  };
  const setOpen = (key: string, open: boolean) =>
    setUserOpen(prev => (prev[key] === open ? prev : { ...prev, [key]: open }));

  const icaoOptions = useMemo(
    () =>
      Object.entries(AIRFIELDS).map(([icao, a]) => (
        <option key={icao} value={icao} label={a[2]} />
      )),
    [],
  );

  const sec = (key: string, children: ReactNode, head?: ReactNode) => (
    <Sec sectionKey={key} open={isOpen(key)} onToggle={o => setOpen(key, o)} {...(head ? { head } : {})}>
      {children}
    </Sec>
  );

  return (
    <>
      <datalist id="icao-list">{icaoOptions}</datalist>
      <datalist id="wolken-list">
        {WOLKEN_OPTIONS.map(o => (
          <option key={o} value={o} />
        ))}
      </datalist>
      <datalist id="belag-list">
        {belagOptions(t).map(o => (
          <option key={o.value} value={o.value} {...(o.text ? { label: o.text } : {})} />
        ))}
      </datalist>

      <nav id="secnav" aria-label={t('form.sections')}>
        {SECTIONS.map(s => (
          <a key={s.key} href={'#sec-' + s.key} onClick={() => setOpen(s.key, true)}>
            {t('sec.' + s.key + '.nav')}
          </a>
        ))}
      </nav>

      <main id="formview" className={narrow ? 'pnarrow' : undefined} aria-label={t('form.view')}>
        {sec(
          'flug',
          <Row>
            <DateField id="datum" text={t('form.datum')} width={150} />
            <FF id="von" text={t('form.von')} width={130} list="icao-list" />
            <FF id="nach" text={t('form.nach')} width={130} list="icao-list" />
            <FF id="alt_name" text={t('lbl.alt_name')} width={130} list="icao-list" />
            <FF id="lfz_muster" text={t('lbl.lfz_muster')} width={110} />
            <FF id="lfz_kennz" text={t('lbl.lfz_kennz')} width={110} placeholder={t('form.lfz_kennz.ph')} />
            <AircraftPicker
              aircraft={aircraft}
              aircraftId={aircraftId}
              {...(onPickAircraft ? { onPick: onPickAircraft } : {})}
            />
          </Row>,
        )}

        {sec(
          'route',
          <>
            <RouteTable />
            <LegCards />
            <div className="note">{t('form.route.note')}</div>
            <WindsBox />
            <TerrainBox />
          </>,
          <>
            <Freshness family="winds" />
            <Freshness family="terrain" />
          </>,
        )}

        {sec(
          'zeit',
          <>
            <Row>
              <FF id="etd" text={t('form.etd')} width={74} placeholder="HH:MM" />
              <FF id="atd" text={t('form.atd')} width={74} placeholder="HH:MM" />
              <FF id="eta" text={t('form.eta')} width={74} placeholder="HH:MM" />
              <FF id="ata" text={t('form.ata')} width={74} placeholder="HH:MM" />
              <FF id="ss" text={t('lbl.ss')} width={90} placeholder="HH:MM" />
              <FF id="safe_time" text={t('form.safe_time')} width={90} placeholder="HH:MM" />
            </Row>
          </>,
        )}

        {sec(
          'wx',
          <>
            <Row>
              <CheckField id="gafor_check" text={t('form.gafor_check')} />
              <FF id="gafor_bis" text={t('form.gafor_bis')} width={80} placeholder="HH:MM" />
              <FF id="gafor" text={t('form.gafor')} width={220} />
            </Row>
            <div className="note">{t('form.wx.note')}</div>
            <div className="note">{t('form.wx.lines')}</div>
            <Lines prefix="wetter_" n={6} />
            <WetterAuto />
          </>,
          // the same briefing as the ATIS block's, and deliberately without a second
          // ⟳: one source, one refresh
          <Freshness family="wx" action={false} label={false} />,
        )}

        {sec(
          'atis',
          <>
            {ATIS_ROWS.map(r => (
              <Row key={r}>
                <FF id={'atis' + r + '_station'} text={t('form.atis.station')} width={110} list="icao-list" />
                <FF id={'atis' + r + '_code'} text={t('form.atis.code')} width={46} />
                <FF id={'atis' + r + '_rwy'} text={t('form.atis.rwy')} width={52} />
                <FF id={'atis' + r + '_tl'} text={t('form.atis.tl')} width={46} />
                <FF id={'atis' + r + '_zeit'} text={t('form.atis.zeit')} width={60} />
                <FF id={'atis' + r + '_wind'} text={t('form.atis.wind')} width={70} />
                <FF id={'atis' + r + '_sicht'} text={t('form.atis.sicht')} width={62} />
                <FF id={'atis' + r + '_wolken'} text={t('form.atis.wolken')} width={120} list="wolken-list" />
                <FF id={'atis' + r + '_temp'} text={t('form.atis.temp')} width={70} />
                <FF id={'atis' + r + '_qnh'} text={t('form.atis.qnh')} width={52} />
                <FF id={'atis' + r + '_tendenz'} text={t('form.atis.tendenz')} width={150} />
              </Row>
            ))}
            <AtisWx />
          </>,
          <Freshness family="wx" />,
        )}

        {sec('b1', <Lines prefix="freigaben_" n={4} />)}
        {sec('v5', <Lines prefix="fs_beratung_" n={6} />)}

        {sec(
          'fq',
          <>
            <Row>
              <FF id="fq_dep_ground" text={t('form.dep') + ': ' + t('fq.ground')} width={90} />
              <FF id="fq_dep_twr" text={t('form.dep') + ': ' + t('fq.twr')} width={90} />
              <FF id="fq_dep_info" text={t('form.dep') + ': ' + t('fq.info')} width={90} />
            </Row>
            <Row>
              <FF id="fq_dest_twr" text={t('form.dest') + ': ' + t('fq.twr')} width={90} />
              <FF id="fq_dest_ground" text={t('form.dest') + ': ' + t('fq.ground')} width={90} />
              <FF id="fq_dest_info" text={t('form.dest') + ': ' + t('fq.info')} width={90} />
            </Row>
          </>,
        )}

        {sec(
          'aip',
          <>
            {AIP_ROWS.map(([k, textKey]) => (
              <Row key={k}>
                <span className="note alab">{t(textKey)}</span>
                {AIP_CELLS.map(([c, width]) => (
                  <FF key={c} id={'aip_' + k + '_' + c} text={t('form.aip.' + c)} width={width} />
                ))}
                {/* the AIP prints whatever it prints (CONC, PEM, "ASPH/Gras"): suggestions, not a closed list */}
                <FF id={'aip_' + k + '_belag'} text={t('form.aip.belag')} width={110} list="belag-list" />
                <SelectField
                  id={'aip_' + k + '_gras'}
                  text={t('form.aip.gras')}
                  options={grasOptions(t)}
                  width={80}
                  emptyText={t('no')}
                />
                <FF id={'aip_' + k + '_surface'} text={t('form.aip.surface')} width={160} />
                <FF id={'aip_' + k + '_restr'} text={t('form.aip.restr')} width={160} />
              </Row>
            ))}
            {/* openAIP publishes under CC BY-NC 4.0: wherever its figures are shown,
                the credit is shown with them. The licence itself is one click away,
                behind the ⓘ in this section's header. */}
            <p className="note wxcredit">
              <a className="cite" href="https://www.openaip.net/" target="_blank" rel="noopener noreferrer">
                {t('geo.credit')}
              </a>
            </p>
          </>,
          <Freshness family="geo" />,
        )}

        {sec(
          'fuel',
          <>
            {FUEL_ROWS.map(([k, zeit, liter]) => (
              <Row className="fuelrow" key={k}>
                <span className="note rlab">{t('form.fuel.' + k)}</span>
                {zeit ? (
                  <FF
                    id={'fuel_' + k + '_zeit'}
                    text={t('fuel.zeit')}
                    width={70}
                    placeholder={k === 'vorrat' ? 'HH:MM' : ':MM'}
                  />
                ) : (
                  <span className="note xmark">—</span>
                )}
                {liter ? <FF id={'fuel_' + k + '_l'} text={t('fuel.liter')} width={70} /> : null}
              </Row>
            ))}
            <Row className="fuelrow">
              <span className="note rlab">{t('form.fuel.safe')}</span>
              <FF id="fuel_safe_time" text={t('fuel.zeit')} width={70} placeholder="HH:MM" />
            </Row>
            <Row>
              <FF id="fuel_verbrauch_1" text={t('form.fuel.verbrauch')} width={200} />
              <FF id="fuel_verbrauch_2" text={t('form.line', { n: 2 })} width={200} />
            </Row>
          </>,
        )}

        {sec(
          'mb',
          <>
            <Row>
              <span className="note rlab narrow">{t('form.mb.units')}</span>
              <SelectField id="mb_unit_masse" text={t('form.mb.unit_masse')} options={MASSE_OPTIONS} width={100} />
              <SelectField id="mb_unit_moment" text={t('form.mb.unit_moment')} options={MOMENT_OPTIONS} width={110} />
            </Row>
            {MB_ROWS.map(([k, sum]) => (
              <Row className="fuelrow" key={k}>
                <span className={'note rlab narrow' + (sum ? ' b' : '')}>{(sum ? '= ' : '') + t('mb.' + k)}</span>
                <FF id={'mb_' + k + '_m'} text={t('mb.masse')} width={80} />
                <FF id={'mb_' + k + '_mom'} text={t('mb.moment')} width={80} />
              </Row>
            ))}
          </>,
        )}

        {sec(
          'tl',
          <>
            <Row className="tlhead">
              <span className="note rlab narrow" />
              {TL_COLS.map(c => (
                <span className="note tlcol" key={c}>
                  {t('tl.' + c)}
                </span>
              ))}
            </Row>
            {TL_ROWS.map(([k, cols]) => (
              <Row className="tlrow" key={k}>
                <span className="note rlab narrow">{t('tl.' + k)}</span>
                {TL_COLS.map(c =>
                  cols.includes(c) ? (
                    <FieldInput key={c} id={'tl_' + k + '_' + c} width={90} />
                  ) : (
                    <span className="note xmark" key={c}>
                      ×
                    </span>
                  ),
                )}
              </Row>
            ))}
            <div className="note">{t('form.tl.note')}</div>
          </>,
        )}
      </main>
    </>
  );
}
